import { useEffect, useRef } from "react";

/**
 * Read a JSON object previously saved with `usePersistedEffect` under `key`,
 * shallow-merged onto `fallback` so newly-added fields fall back to their
 * default instead of becoming `undefined` for users with an older saved shape.
 * Call once (e.g. in a `useState(() => ...)` initializer) — this does not
 * subscribe to changes.
 */
export function loadPersisted<T extends object>(key: string, fallback: T): T {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return fallback;
        return { ...fallback, ...JSON.parse(raw) };
    } catch {
        return fallback;
    }
}

/**
 * Persist `value` to localStorage under `key` whenever it changes, debounced
 * so rapid updates (e.g. typing) don't hammer localStorage on every keystroke.
 * Pairs with `loadPersisted` for restoring on next mount. Fails silently if
 * localStorage is unavailable (private browsing, quota exceeded) — this is
 * a convenience feature, not something that should break the page.
 */
export function usePersistedEffect<T>(
    key: string,
    value: T,
    debounceMs = 300,
): void {
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
            try {
                localStorage.setItem(key, JSON.stringify(value));
            } catch {
                // Non-critical — silently skip persistence
            }
        }, debounceMs);

        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key, value, debounceMs]);
}

/** Save `value` under `key` right away (for a one-off save, e.g. after a
 * form is submitted). Fails silently, like the rest of this file. */
export function savePersisted<T>(key: string, value: T): void {
    try {
        localStorage.setItem(key, JSON.stringify(value));
    } catch {
        // Non-critical
    }
}

/** Remove a previously persisted value. */
export function clearPersisted(key: string): void {
    try {
        localStorage.removeItem(key);
    } catch {
        // Non-critical
    }
}
